<template>
  <ActionButton
    class="shipment-action-button"
    :actions-by-status="actionsByStatus"
    :status="shipment.status"
    :loading="loading || printing || checkCallStatus.loading"
  />
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import { computed, PropType, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  ActionCommand,
  CarrierSetting,
  ReferenceNumberTypeWellKnownNaturalId,
  RygenMenuItem,
} from '@rygen/rygen-ui/model';

import {
  useDoNotDispatch,
  useLowestCostReasonCode,
  useShipmentStops,
} from '@/composables';
import {
  CargoInsuranceStatus,
  P44_RATE_STRATEGIES,
  RateQuote,
  ReferenceNumber,
  Shipment,
  ShipmentAction,
  ShipmentEvent,
  ShipmentEventType,
  ShipmentStatus,
  WaterfallTender,
  WaterfallTenderStatus,
  WellKnownRateStrategy,
} from '@/model';
import { useCheckCallStore } from '@/stores/CheckCall';
import { useCreditUsageStore } from '@/stores/CreditUsage';
import { isStatusEqualOrBefore } from '@/util';

import ActionButton from '@rygen/rygen-ui/components/button/ActionButton.vue';

const props = defineProps({
  shipment: {
    type: Shipment,
    required: true,
  },
  shipmentEvents: {
    type: Array as PropType<ShipmentEvent[]>,
    default: () => [],
  },
  carrierSettings: {
    type: CarrierSetting,
    default: () => new CarrierSetting(),
  },
  acceptedRateQuote: {
    type: Object as PropType<RateQuote | undefined>,
    default: () => undefined,
    required: false,
  },
  waterfallTender: {
    type: Object as PropType<WaterfallTender | undefined>,
    default: () => undefined,
    required: false,
  },
  cargoInsuranceStatus: {
    type: String as PropType<CargoInsuranceStatus | undefined>,
    default: () => undefined,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  printing: {
    type: Boolean,
    default: () => false,
  },
  rateShopOnly: {
    type: Boolean,
    default: () => false,
  },
  carrierDirectCredentialsPresent: {
    type: Boolean,
    default: () => false,
  },
  macroPointCredentialsPresent: {
    type: Boolean,
    default: () => false,
  },
});
const {
  shipment,
  shipmentEvents,
  carrierDirectCredentialsPresent,
  acceptedRateQuote,
  cargoInsuranceStatus,
  loading,
  printing,
  rateShopOnly,
  waterfallTender,
  macroPointCredentialsPresent,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'command', payload: ActionCommand<ShipmentAction>): void;
}>();

const isTransportSegmentEnabled = useFlag(
  'route-builder-transport-segment-feat-flag',
);
const isShipmentBuilderEnabled = useFlag('shipment-builder-feat-flag');
const isWaterfallTenderingEnabled = useFlag('waterfallTendering');

const { checkCallStatus } = useCheckCallStore();
const { canTenderToCarrier } = useCreditUsageStore();
const { isDoNotDispatchEnabled, isRateQuoteDispatchable } = useDoNotDispatch();
const { t } = useI18n();
const { firstUnresolvedMilestone, lastResolvedMilestone } = useShipmentStops(
  shipment,
  shipmentEvents,
);
const { isRateSelectionReasonRequired } = useLowestCostReasonCode();

const resendPrefix = computed(() => {
  if (shipment.value.status === ShipmentStatus.TENDER_REJECTED) {
    return 'Resend ';
  }
  return '';
});

const apiTrackable = computed(() => {
  const scac = acceptedRateQuote?.value?.carrier?.scac;
  if (!scac) {
    return false;
  }

  return carrierDirectCredentialsPresent.value;
});

const macroPointTrackingAvailable = computed(() => {
  const alreadyTracking =
    shipment.value.referenceNumbers.find(
      (r: ReferenceNumber) =>
        r.typeNaturalId ===
          ReferenceNumberTypeWellKnownNaturalId.MACROPOINT_ORDER_ID ||
        r.typeNaturalId ===
          ReferenceNumberTypeWellKnownNaturalId.MACROPOINT_TRACKING_REQUEST_ID,
    ) != null;

  return macroPointCredentialsPresent.value && !alreadyTracking;
});

const hasRemoveOrderPermissions = computed(() => {
  if (!authorization.hasAuthority('shipment', 'update')) {
    return false;
  }
  if (
    isStatusEqualOrBefore(
      shipment.value.status,
      ShipmentStatus.CARRIER_ASSIGNED,
    )
  ) {
    return true;
  }
  if (
    isStatusEqualOrBefore(shipment.value.status, ShipmentStatus.BOOKED) &&
    authorization.hasAuthority('shipment', 'updateBookedShipmentLocations')
  ) {
    return true;
  }
  if (authorization.hasAuthority('shipment', 'updateLockedFields')) {
    return true;
  }

  return false;
});

// It seems disabled prop in actions does not work properly (disabled action also disabled "more option" dropdown and
// disabled action in dropdown option are still clickable even though it looks disabled).
// For the time being, instead of disabling the action, tendering actions will not be visible if this is true
const isTenderingDisabledForDoNotDispatch = computed(() => {
  const isTenderingDisabled = !isRateQuoteDispatchable(
    acceptedRateQuote?.value,
  );

  // Do Not Dispatch cannot be overriden for waterfall tendering
  const isWaterfallTenderingDisabled =
    isDoNotDispatchEnabled &&
    acceptedRateQuote?.value?.doNotDispatch &&
    waterfallTender?.value;

  return isTenderingDisabled || isWaterfallTenderingDisabled;
});

const createEmptyAction = (action: ShipmentAction) => ({
  key: action,
  label: '',
  command: () => emit('command', { action }),
  visible: false,
});

const actions: Ref<{
  [shipmentAction in ShipmentAction]: RygenMenuItem;
}> = computed(() => ({
  [ShipmentAction.ADD_TAGS]: createEmptyAction(ShipmentAction.ADD_TAGS),
  [ShipmentAction.CANCEL]: {
    key: ShipmentAction.CANCEL,
    label: t('common.cancelParam', { param: t('shipments.shipments') }),
    icon: 'pi pi-times',
    command: () => emit('command', { action: ShipmentAction.CANCEL }),
    requiredAuthorization: ['shipment', 'cancel'],
    visible:
      !rateShopOnly.value &&
      (isStatusEqualOrBefore(shipment.value.status, ShipmentStatus.BOOKED) ||
        shipment.value.customerRouted),
  },
  [ShipmentAction.ASSIGN_USERS]: createEmptyAction(ShipmentAction.ASSIGN_USERS),
  [ShipmentAction.CANCEL_CHECK_CALL_EMAILS]: {
    key: ShipmentAction.CANCEL_CHECK_CALL_EMAILS,
    label: t('common.cancelParam', {
      param: t('carrierPortal.checkCallReminders.checkCallReminders'),
    }),
    icon: 'pi pi-times',
    command: () =>
      emit('command', { action: ShipmentAction.CANCEL_CHECK_CALL_EMAILS }),
    requiredAuthorization: ['shipment', 'update'],
    visible: !!checkCallStatus.value.data?.active,
  },
  [ShipmentAction.CANCEL_WATERFALL_TENDER]: {
    key: ShipmentAction.CANCEL_WATERFALL_TENDER,
    label: t('common.cancelParam', {
      param: t('waterfallTender.waterfallTender'),
    }),
    icon: PrimeIcons.TIMES,
    command: () =>
      emit('command', { action: ShipmentAction.CANCEL_WATERFALL_TENDER }),
    requiredAuthorization: ['shipment', 'update'],
    visible:
      isWaterfallTenderingEnabled.value &&
      (waterfallTender?.value?.status === WaterfallTenderStatus.IN_PROGRESS ||
        waterfallTender?.value?.status ===
          WaterfallTenderStatus.COMPLETED_WITHOUT_ACCEPTED_RATE_QUOTE),
  },
  [ShipmentAction.CARRIER_UNASSIGN]: {
    key: ShipmentAction.CARRIER_UNASSIGN,
    label: t('common.removeParam', { param: t('carriers.carriers') }),
    icon: 'pi pi-times',
    command: () => emit('command', { action: ShipmentAction.CARRIER_UNASSIGN }),
    requiredAuthorization: ['shipment', 'update'],
    visible:
      !waterfallTender?.value?.isInProgress() &&
      waterfallTender?.value?.status !==
        WaterfallTenderStatus.COMPLETED_WITHOUT_ACCEPTED_RATE_QUOTE,
  },
  [ShipmentAction.CONSOLIDATE]: {
    key: ShipmentAction.CONSOLIDATE,
    label: t('shipments.consolidateShipment'),
    icon: 'pi pi-link',
    command: () => emit('command', { action: ShipmentAction.CONSOLIDATE }),
    requiredAuthorization: ['shipment', 'update'],
    visible: !rateShopOnly.value,
  },
  [ShipmentAction.EMAIL_DOCUMENTS]: {
    key: ShipmentAction.EMAIL_DOCUMENTS,
    label: t('documents.emailDocuments'),
    icon: 'pi pi-send',
    command: () => emit('command', { action: ShipmentAction.EMAIL_DOCUMENTS }),
    visible: !rateShopOnly.value,
  },
  [ShipmentAction.EVENT_DELIVERED_CREATE]: {
    key: ShipmentAction.EVENT_DELIVERED_CREATE,
    label: t('shipments.addDelivery'),
    icon: 'pi pi-check',
    command: () =>
      emit('command', { action: ShipmentAction.EVENT_DELIVERED_CREATE }),
    requiredAuthorization: ['shipmentEvent', 'update'],
    visible: !rateShopOnly.value,
  },
  [ShipmentAction.EVENT_DELIVERED_UNDO]: {
    key: ShipmentAction.EVENT_DELIVERED_UNDO,
    label: t('shipments.notDelivered'),
    icon: 'pi pi-backward',
    command: () =>
      emit('command', { action: ShipmentAction.EVENT_DELIVERED_UNDO }),
    requiredAuthorization: ['shipmentEvent', 'update'],
    visible: !rateShopOnly.value && !shipment.value.customerRouted,
  },
  [ShipmentAction.EVENT_MISC_CREATE]: {
    key: ShipmentAction.EVENT_MISC_CREATE,
    label: t('shipments.addEvent'),
    icon: 'pi pi-plus',
    command: () =>
      emit('command', { action: ShipmentAction.EVENT_MISC_CREATE }),
    requiredAuthorization: ['shipmentEvent', 'update'],
    visible: !rateShopOnly.value,
  },
  [ShipmentAction.EVENT_PICKUP_CREATE]: {
    key: ShipmentAction.EVENT_PICKUP_CREATE,
    label: t('shipments.addPickup'),
    icon: 'pi pi-plus',
    command: () =>
      emit('command', { action: ShipmentAction.EVENT_PICKUP_CREATE }),
    requiredAuthorization: ['shipmentEvent', 'update'],
    visible: !rateShopOnly.value,
  },
  [ShipmentAction.EVENT_PICKUP_UNDO]: {
    key: ShipmentAction.EVENT_PICKUP_UNDO,
    label: t('shipments.notPickedUp'),
    icon: 'pi pi-backward',
    command: () =>
      emit('command', { action: ShipmentAction.EVENT_PICKUP_UNDO }),
    requiredAuthorization: ['shipmentEvent', 'update'],
    visible: !rateShopOnly.value && !shipment.value.customerRouted,
  },
  [ShipmentAction.EVENT_SERVICE_FAILURE_CREATE]: {
    key: ShipmentAction.EVENT_SERVICE_FAILURE_CREATE,
    label: t('shipments.addServiceFailure'),
    icon: 'pi pi-exclamation-triangle',
    command: () =>
      emit('command', { action: ShipmentAction.EVENT_SERVICE_FAILURE_CREATE }),
    requiredAuthorization: ['shipmentEvent', 'update'],
    visible: !rateShopOnly.value,
  },
  [ShipmentAction.INITIATE_MACROPOINT_TRACKING]: {
    key: ShipmentAction.INITIATE_MACROPOINT_TRACKING,
    icon: 'pi pi-compass',
    label: t('shipments.macroPoint.initiateMacroPointTracking'),
    command: () =>
      emit('command', { action: ShipmentAction.INITIATE_MACROPOINT_TRACKING }),
    requiredAuthorization: ['shipment', 'update'],
    visible: macroPointTrackingAvailable.value,
  },
  [ShipmentAction.PRINT_BOL]: {
    key: ShipmentAction.PRINT_BOL,
    label: t('shipments.printBol'),
    icon: PrimeIcons.PRINT,
    command: () => emit('command', { action: ShipmentAction.PRINT_BOL }),
    disabled: printing.value,
    visible: !rateShopOnly.value,
  },
  [ShipmentAction.PRINT_SHIPPING_LABEL_MODAL]: {
    key: ShipmentAction.PRINT_SHIPPING_LABEL_MODAL,
    label: t('shipments.printShippingLabel'),
    icon: PrimeIcons.PRINT,
    command: () =>
      emit('command', { action: ShipmentAction.PRINT_SHIPPING_LABEL_MODAL }),
    disabled: printing.value,
    visible: !rateShopOnly.value,
  },
  [ShipmentAction.REMOVE_ORDERS]: {
    key: ShipmentAction.REMOVE_ORDERS,
    label: t('common.removeParam', { param: t('orders.order(s)') }),
    icon: 'pi pi-times',
    command: () => emit('command', { action: ShipmentAction.REMOVE_ORDERS }),
    requiredAuthorization: ['shipment', 'update'],
    visible: hasRemoveOrderPermissions.value,
  },
  [ShipmentAction.REQUEST_DOCUMENTS]: {
    key: ShipmentAction.REQUEST_DOCUMENTS,
    label: t('shipments.requestDocuments'),
    icon: 'pi pi-file-pdf',
    command: () =>
      emit('command', { action: ShipmentAction.REQUEST_DOCUMENTS }),
    requiredAuthorization: ['shipment', 'update'],
    visible: carrierDirectCredentialsPresent.value,
  },
  [ShipmentAction.REQUEST_SPOT_MARKET_BID]: {
    key: ShipmentAction.REQUEST_SPOT_MARKET_BID,
    label: t('carrierPortal.spotMarket.requestBid'),
    icon: PrimeIcons.DOLLAR,
    command: () =>
      emit('command', { action: ShipmentAction.REQUEST_SPOT_MARKET_BID }),
  },
  [ShipmentAction.REQUEST_TRACKING]: {
    key: ShipmentAction.REQUEST_TRACKING,
    label: t('shipments.requestTracking'),
    icon: PrimeIcons.MAP,
    command: () => emit('command', { action: ShipmentAction.REQUEST_TRACKING }),
    visible: () => {
      const rateStrategyType = acceptedRateQuote?.value?.rateStrategyType ?? '';
      return (
        P44_RATE_STRATEGIES.includes(
          rateStrategyType as WellKnownRateStrategy,
        ) || apiTrackable.value
      );
    },
  },
  [ShipmentAction.RESEND_CONFIRMATION]: {
    key: ShipmentAction.RESEND_CONFIRMATION,
    label: t('shipments.resendConfirmation'),
    icon: 'pi pi-send',
    command: () =>
      emit('command', { action: ShipmentAction.RESEND_CONFIRMATION }),
    requiredAuthorization: ['shipment', 'update'],
  },
  [ShipmentAction.SEND_CHECK_CALL_REQUEST]: {
    key: ShipmentAction.SEND_CHECK_CALL_REQUEST,
    label: t('carrierPortal.checkCallReminders.send'),
    icon: 'pi pi-send',
    command: () =>
      emit('command', { action: ShipmentAction.SEND_CHECK_CALL_REQUEST }),
    requiredAuthorization: ['shipment', 'read'],
    visible: checkCallStatus.value.data?.active === false,
  },
  [ShipmentAction.SEND_TO_ROUTE_BUILDER]: {
    key: ShipmentAction.SEND_TO_ROUTE_BUILDER,
    label: t('routeBuilder.sendToRouteBuilder'),
    icon: PrimeIcons.TRUCK,
    to: {
      name: 'routeBuilder',
      query: {
        shipmentId: shipment.value.id,
      },
    },
    requiredAuthorization: ['shipment', 'consolidate'],
    visible: isTransportSegmentEnabled.value,
  },
  [ShipmentAction.SEND_TO_SHIPMENT_BUILDER]: {
    key: ShipmentAction.SEND_TO_SHIPMENT_BUILDER,
    label: t('shipments.sendToShipmentBuilder'),
    icon: 'pi pi-truck',
    to: {
      name: 'shipOrderConsolidation',
      query: {
        shipmentId: shipment.value.id,
      },
    },
    requiredAuthorization: ['shipment', 'consolidate'],
    visible: isShipmentBuilderEnabled.value,
  },
  [ShipmentAction.SHARE]: {
    key: ShipmentAction.SHARE,
    label: t('common.shareParam', {
      param: t('shipments.trackingLink'),
    }),
    icon: 'pi pi-user-plus',
    command: () => emit('command', { action: ShipmentAction.SHARE }),
    requiredAuthorization: ['shipment', 'read'],
  },
  [ShipmentAction.TENDER_CONFIRM]: {
    key: ShipmentAction.TENDER_CONFIRM,
    label: t('shipments.confirmTender'),
    icon: 'pi pi-check',
    command: () => emit('command', { action: ShipmentAction.TENDER_CONFIRM }),
    requiredAuthorization: ['shipment', 'update'],
    visible: !isTenderingDisabledForDoNotDispatch.value,
  },
  [ShipmentAction.TENDER_REJECT]: {
    key: ShipmentAction.TENDER_REJECT,
    label: t('shipments.rejectTender'),
    icon: 'pi pi-minus-circle',
    command: () => emit('command', { action: ShipmentAction.TENDER_REJECT }),
    requiredAuthorization: ['shipment', 'update'],
  },
  [ShipmentAction.TENDER_RESEND]: {
    key: ShipmentAction.TENDER_RESEND,
    label: t('shipments.resendTender'),
    icon: 'pi pi-send',
    command: () => emit('command', { action: ShipmentAction.TENDER_RESEND }),
    requiredAuthorization: ['shipment', 'update'],
  },
  [ShipmentAction.TENDER_TO_CARRIER]: {
    key: ShipmentAction.TENDER_TO_CARRIER,
    label: resendPrefix.value + t('shipments.tenderToCarrier'),
    icon: 'pi pi-send',
    command: () =>
      emit('command', { action: ShipmentAction.TENDER_TO_CARRIER }),
    requiredAuthorization: ['shipment', 'update'],
    visible:
      !isTenderingDisabledForDoNotDispatch.value && canTenderToCarrier.value,
  },
  [ShipmentAction.UPDATE_HEALTH]: createEmptyAction(
    ShipmentAction.UPDATE_HEALTH,
  ),
  [ShipmentAction.WATERFALL_TENDER]: {
    key: ShipmentAction.WATERFALL_TENDER,
    label: t('waterfallTender.waterfallTender'),
    icon: PrimeIcons.LIST,
    command: () => emit('command', { action: ShipmentAction.WATERFALL_TENDER }),
    requiredAuthorization: ['shipment', 'update'],
    visible:
      isWaterfallTenderingEnabled.value &&
      !waterfallTender?.value &&
      !isRateSelectionReasonRequired.value,
  },
  [ShipmentAction.CARGO_INSURANCE_PURCHASE]: {
    key: ShipmentAction.CARGO_INSURANCE_PURCHASE,
    label: t('shipments.insurance.getInsuranceQuote'),
    icon: 'pi pi-money-bill',
    command: () =>
      emit('command', { action: ShipmentAction.CARGO_INSURANCE_PURCHASE }),
    requiredAuthorization: ['shipment', 'purchaseCargoInsurance'],
    visible: cargoInsuranceStatus?.value !== 'CERTIFICATE_ISSUED',
  },
  [ShipmentAction.CARGO_INSURANCE_CANCEL]: {
    key: ShipmentAction.CARGO_INSURANCE_CANCEL,
    label: t('cargoInsurance.actions.cancelPolicy'),
    icon: 'pi pi-money-bill',
    command: () =>
      emit('command', { action: ShipmentAction.CARGO_INSURANCE_CANCEL }),
    requiredAuthorization: ['shipment', 'purchaseCargoInsurance'],
    visible: cargoInsuranceStatus?.value === 'CERTIFICATE_ISSUED',
  },
}));

const resolveNextMilestoneAction = computed(() => {
  switch (firstUnresolvedMilestone.value?.eventType) {
    case ShipmentEventType.CARRIER_PICKED_UP:
      return actions.value[ShipmentAction.EVENT_PICKUP_CREATE];

    case ShipmentEventType.DROPOFF:
      return actions.value[ShipmentAction.EVENT_DELIVERED_CREATE];

    default:
      return undefined;
  }
});

const resolveLatestResolvedMilestoneAction = computed(() => {
  switch (lastResolvedMilestone.value?.eventType) {
    case ShipmentEventType.CARRIER_PICKED_UP:
      return actions.value[ShipmentAction.EVENT_PICKUP_UNDO];

    case ShipmentEventType.DROPOFF:
      return actions.value[ShipmentAction.EVENT_DELIVERED_UNDO];

    default:
      return undefined;
  }
});

const actionsByStatus: Ref<{
  [shipmentStatus in ShipmentStatus]: RygenMenuItem[];
}> = computed(() => ({
  [ShipmentStatus.PLANNING]: [
    actions.value[ShipmentAction.SEND_TO_ROUTE_BUILDER],
    actions.value[ShipmentAction.SEND_TO_SHIPMENT_BUILDER],
    actions.value[ShipmentAction.REQUEST_SPOT_MARKET_BID],
    actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
    actions.value[ShipmentAction.SHARE],
    actions.value[ShipmentAction.CARGO_INSURANCE_PURCHASE],
    actions.value[ShipmentAction.CARGO_INSURANCE_CANCEL],
    actions.value[ShipmentAction.WATERFALL_TENDER],
    { separator: true },
    actions.value[ShipmentAction.CANCEL_WATERFALL_TENDER],
    actions.value[ShipmentAction.REMOVE_ORDERS],
    actions.value[ShipmentAction.CANCEL],
  ],
  [ShipmentStatus.CARRIER_ASSIGNED]: [
    actions.value[ShipmentAction.TENDER_TO_CARRIER],
    { separator: true },
    actions.value[ShipmentAction.PRINT_BOL],
    actions.value[ShipmentAction.EMAIL_DOCUMENTS],
    actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
    actions.value[ShipmentAction.SHARE],
    actions.value[ShipmentAction.CARGO_INSURANCE_PURCHASE],
    actions.value[ShipmentAction.CARGO_INSURANCE_CANCEL],
    { separator: true, visible: !rateShopOnly.value },
    actions.value[ShipmentAction.CANCEL_WATERFALL_TENDER],
    actions.value[ShipmentAction.REMOVE_ORDERS],
    actions.value[ShipmentAction.CARRIER_UNASSIGN],
    actions.value[ShipmentAction.CANCEL],
  ],
  [ShipmentStatus.TENDERED]: [
    actions.value[ShipmentAction.TENDER_CONFIRM],
    actions.value[ShipmentAction.TENDER_REJECT],
    actions.value[ShipmentAction.TENDER_RESEND],
    { separator: true },
    actions.value[ShipmentAction.PRINT_BOL],
    actions.value[ShipmentAction.EMAIL_DOCUMENTS],
    actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
    actions.value[ShipmentAction.SHARE],
    actions.value[ShipmentAction.CARGO_INSURANCE_PURCHASE],
    actions.value[ShipmentAction.CARGO_INSURANCE_CANCEL],
    { separator: true, visible: !rateShopOnly.value },
    actions.value[ShipmentAction.CANCEL_WATERFALL_TENDER],
    actions.value[ShipmentAction.REMOVE_ORDERS],
    actions.value[ShipmentAction.CARRIER_UNASSIGN],
    actions.value[ShipmentAction.CANCEL],
  ],
  [ShipmentStatus.TENDER_REJECTED]: [
    actions.value[ShipmentAction.TENDER_TO_CARRIER],
    { separator: true },
    actions.value[ShipmentAction.PRINT_BOL],
    actions.value[ShipmentAction.EMAIL_DOCUMENTS],
    actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
    actions.value[ShipmentAction.SHARE],
    actions.value[ShipmentAction.CARGO_INSURANCE_PURCHASE],
    actions.value[ShipmentAction.CARGO_INSURANCE_CANCEL],
    { separator: true, visible: !rateShopOnly.value },
    actions.value[ShipmentAction.CANCEL_WATERFALL_TENDER],
    actions.value[ShipmentAction.REMOVE_ORDERS],
    actions.value[ShipmentAction.CARRIER_UNASSIGN],
    actions.value[ShipmentAction.CANCEL],
  ],
  [ShipmentStatus.BOOKED]: [
    resolveNextMilestoneAction.value ??
      actions.value[ShipmentAction.EVENT_PICKUP_CREATE],
    actions.value[ShipmentAction.EVENT_MISC_CREATE],
    actions.value[ShipmentAction.RESEND_CONFIRMATION],
    actions.value[ShipmentAction.SHARE],
    actions.value[ShipmentAction.CARGO_INSURANCE_PURCHASE],
    actions.value[ShipmentAction.CARGO_INSURANCE_CANCEL],
    actions.value[ShipmentAction.REQUEST_TRACKING],
    actions.value[ShipmentAction.REQUEST_DOCUMENTS],
    actions.value[ShipmentAction.INITIATE_MACROPOINT_TRACKING],
    actions.value[ShipmentAction.SEND_CHECK_CALL_REQUEST],
    actions.value[ShipmentAction.CANCEL_CHECK_CALL_EMAILS],
    actions.value[ShipmentAction.EMAIL_DOCUMENTS],
    actions.value[ShipmentAction.PRINT_BOL],
    actions.value[ShipmentAction.EVENT_SERVICE_FAILURE_CREATE],
    actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
    { separator: true },
    actions.value[ShipmentAction.REMOVE_ORDERS],
    actions.value[ShipmentAction.CARRIER_UNASSIGN],
    actions.value[ShipmentAction.CANCEL],
  ],
  [ShipmentStatus.IN_TRANSIT]: [
    resolveNextMilestoneAction.value ??
      actions.value[ShipmentAction.EVENT_DELIVERED_CREATE],
    actions.value[ShipmentAction.EVENT_MISC_CREATE],
    actions.value[ShipmentAction.RESEND_CONFIRMATION],
    actions.value[ShipmentAction.SHARE],
    actions.value[ShipmentAction.REQUEST_TRACKING],
    actions.value[ShipmentAction.REQUEST_DOCUMENTS],
    actions.value[ShipmentAction.INITIATE_MACROPOINT_TRACKING],
    actions.value[ShipmentAction.SEND_CHECK_CALL_REQUEST],
    actions.value[ShipmentAction.CANCEL_CHECK_CALL_EMAILS],
    actions.value[ShipmentAction.PRINT_BOL],
    actions.value[ShipmentAction.EMAIL_DOCUMENTS],
    actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
    { separator: true },
    resolveLatestResolvedMilestoneAction.value ??
      actions.value[ShipmentAction.EVENT_PICKUP_UNDO],
    actions.value[ShipmentAction.EVENT_SERVICE_FAILURE_CREATE],
    actions.value[ShipmentAction.REMOVE_ORDERS],
    actions.value[ShipmentAction.CANCEL],
  ],
  [ShipmentStatus.DELIVERED]: [
    actions.value[ShipmentAction.PRINT_BOL],
    actions.value[ShipmentAction.EMAIL_DOCUMENTS],
    actions.value[ShipmentAction.RESEND_CONFIRMATION],
    actions.value[ShipmentAction.SHARE],
    actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
    actions.value[ShipmentAction.REQUEST_DOCUMENTS],
    { separator: true },
    actions.value[ShipmentAction.EVENT_DELIVERED_UNDO],
    actions.value[ShipmentAction.EVENT_SERVICE_FAILURE_CREATE],
    actions.value[ShipmentAction.REMOVE_ORDERS],
    actions.value[ShipmentAction.CANCEL],
  ],
  [ShipmentStatus.CANCELED]: [],
  [ShipmentStatus.COMPLETE]: [actions.value[ShipmentAction.REQUEST_DOCUMENTS]],
}));
</script>
